import React, { useState, useEffect } from 'react';
import {
    Container,
    Paper,
    Box,
    TextField,
    Button,
    Typography,
    Alert,
    Link,
    InputAdornment,
    IconButton,
    Divider,
    Stack,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import { 
    Visibility, 
    VisibilityOff, 
    Security,
    AccountCircle,
    Description
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const LoginPage: React.FC = () => {
    const navigate = useNavigate();
    const { login } = useAuth();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [formData, setFormData] = useState({
        email: '',
        password: '',
    });
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    // Per-page SEO: branded title + meta description for the login screen
    // (the only crawlable page in this otherwise-authed app).
    useEffect(() => {
        const prevTitle = document.title;
        document.title = 'Sign In | Felice Care Management System';

        const desc = document.querySelector('meta[name="description"]');
        const prevDesc = desc?.getAttribute('content') ?? null;
        desc?.setAttribute(
            'content',
            'Sign in to the Felice Care Management System — staff and participant portal for Felice Care and Disability Services.'
        );

        return () => {
            document.title = prevTitle;
            if (prevDesc !== null) desc?.setAttribute('content', prevDesc);
        };
    }, []);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
        setError('');
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            await login(formData.email, formData.password);
            navigate('/dashboard');
        } catch (err: any) {
            setError(err.message || 'Login failed');
        } finally {
            setLoading(false);
        }
    };

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                width: '100vw',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                margin: 0,
                padding: 0,
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
            }}
        >
            <Container component="main" maxWidth="sm" sx={{ p: 2 }}>
                <Paper 
                    elevation={24} 
                    sx={{ 
                        borderRadius: 4,
                        overflow: 'hidden',
                        background: 'rgba(255, 255, 255, 0.95)',
                        backdropFilter: 'blur(10px)',
                    }}
                >
                    {/* Header Section */}
                    <Box
                        sx={{
                            background: 'linear-gradient(135deg, #6e34c2 0%, #4a148c 80%)',
                            color: 'white',
                            py: 4,
                            px: 4,
                            textAlign: 'center',
                        }}
                    >
                        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                            <img 
                                src="/favicon.ico" 
                                alt="Felice Care Logo" 
                                style={{ 
                                    width: 60, 
                                    height: 60, 
                                    opacity: 0.9,
                                    // filter: 'brightness(0) invert(1)' // Makes the icon white to match the theme
                                }} 
                            />
                        </Box>
                        <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom>
                            Felice Care
                        </Typography>
                        <Typography variant="h6" sx={{ opacity: 0.9, fontWeight: 300 }}>
                            Management System
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1, opacity: 0.8 }}>
                            Streamlining felice care management with excellence
                        </Typography>
                    </Box>

                    {/* Login Form Section */}
                    <Box sx={{ p: 4 }}>
                        <Stack spacing={3}>
                            <Box textAlign="center">
                                <Typography variant="h5" fontWeight="600" color="text.primary" gutterBottom>
                                    Welcome Back
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Sign in to access your dashboard
                                </Typography>
                            </Box>

                            {error && (
                                <Alert 
                                    severity="error" 
                                    sx={{ 
                                        borderRadius: 2,
                                        '& .MuiAlert-icon': {
                                            color: '#d32f2f'
                                        }
                                    }}
                                >
                                    {error}
                                </Alert>
                            )}

                            <Box component="form" onSubmit={handleSubmit}>
                                <Stack spacing={3}>
                                    <TextField
                                        fullWidth
                                        id="email"
                                        label="Email Address"
                                        name="email"
                                        type="email"
                                        autoComplete="email"
                                        autoFocus
                                        value={formData.email}
                                        onChange={handleChange}
                                        variant="outlined"
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <AccountCircle color="action" />
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 2,
                                                '&:hover fieldset': {
                                                    borderColor: theme.palette.primary.main,
                                                },
                                            },
                                        }}
                                    />
                                    
                                    <TextField
                                        fullWidth
                                        name="password"
                                        label="Password"
                                        type={showPassword ? 'text' : 'password'}
                                        id="password"
                                        autoComplete="current-password"
                                        value={formData.password}
                                        onChange={handleChange}
                                        variant="outlined"
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Security color="action" />
                                                </InputAdornment>
                                            ),
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        aria-label="toggle password visibility"
                                                        onClick={togglePasswordVisibility}
                                                        edge="end"
                                                        size="small"
                                                    >
                                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 2,
                                                '&:hover fieldset': {
                                                    borderColor: theme.palette.primary.main,
                                                },
                                            },
                                        }}
                                    />

                                    <Button
                                        type="submit"
                                        fullWidth
                                        variant="contained"
                                        size="large"
                                        disabled={loading}
                                        sx={{
                                            mt: 2,
                                            py: 1.5,
                                            borderRadius: 2,
                                            fontSize: '1.1rem',
                                            fontWeight: 600,
                                            background: 'linear-gradient(135deg, #6e34c2 0%, #4a148c 100%)',
                                            boxShadow: '0 4px 15px rgba(110, 52, 194, 0.3)',
                                            '&:hover': {
                                                background: 'linear-gradient(135deg, #5a2ba6 0%, #3d1178 100%)',
                                                boxShadow: '0 6px 20px rgba(110, 52, 194, 0.4)',
                                                transform: 'translateY(-1px)',
                                            },
                                            '&:disabled': {
                                                background: 'rgba(110, 52, 194, 0.5)',
                                            },
                                            transition: 'all 0.3s ease',
                                        }}
                                    >
                                        {loading ? 'Signing In...' : 'Sign In'}
                                    </Button>
                                </Stack>
                            </Box>

                            <Divider sx={{ my: 2 }} />

                            {/* Documentation Link */}
                            <Box textAlign="center">
                                <Button
                                    variant="outlined"
                                    startIcon={<Description />}
                                    onClick={() => navigate('/documentation')}
                                    sx={{
                                        borderRadius: 2,
                                        textTransform: 'none',
                                        borderColor: theme.palette.primary.main,
                                        color: theme.palette.primary.main,
                                        '&:hover': {
                                            borderColor: theme.palette.primary.dark,
                                            backgroundColor: 'rgba(110, 52, 194, 0.05)',
                                        },
                                    }}
                                >
                                    View System Documentation
                                </Button>
                            </Box>

                            {/* Footer */}
                            <Box textAlign="center" sx={{ mt: 3 }}>
                                <Typography variant="caption" color="text.secondary" display="block">
                                    Visit our website:{' '}
                                    <Link
                                        href="https://felicecare.com.au/"
                                        target="_blank"
                                        rel="noopener"
                                        underline="hover"
                                        sx={{ fontWeight: 500 }}
                                    >
                                        felicecare.com.au
                                    </Link>
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                    © 2025 Felice Care and Disability Services. All rights reserved.
                                </Typography>
                            </Box>
                        </Stack>
                    </Box>
                </Paper>
            </Container>
        </Box>
    );
};

export default LoginPage;
